"use client";

import * as React from "react";
import * as Popover from "@radix-ui/react-popover";
import { DayPicker, DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";

import { getMews, getHotelConfigurationId } from "./mews-client"; // <- adjust path

type Hotel = { id: string; name: string; reviews?: string; status?: string };
const STAYS: Hotel[] = [
  {
    id: "dottingen",
    name: "Döttingen",
    reviews: "1,189 reviews",
    status: "219 rooms",
  },
  // { id: "littau", name: "Littau", reviews: "★ 5.0 (12,850 reviews)", status: "150 rooms" },
];

const cls = (...s: (string | false | undefined)[]) =>
  s.filter(Boolean).join(" ");

function Spinner() {
  return (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function Pill({
  label,
  value,
  active,
  highlight,
  className,
}: {
  label: string;
  value: string;
  active?: boolean;
  highlight?: boolean;
  className?: string;
}) {
  const bg = highlight ? "bg-white shadow-sm" : "bg-transparent";
  const valueColor = active ? "text-gray-900" : "text-gray-500";
  return (
    <div
      className={cls(
        "flex flex-col gap-1 w-full text-left min-w-[140px] px-5 py-3 rounded-[28px] transition",
        bg,
        className,
      )}
    >
      <span className="text-xs leading-none text-gray-700">{label}</span>
      <div className="flex items-center gap-2">
        <span className={cls("text-sm truncate", valueColor)}>{value}</span>
      </div>
    </div>
  );
}

function formatRange(range?: DateRange) {
  if (!range?.from) return "Add dates";
  const fmt: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  const from = new Intl.DateTimeFormat(undefined, fmt).format(range.from);
  const to = range.to
    ? new Intl.DateTimeFormat(undefined, fmt).format(range.to)
    : "—";
  
  // If start and end are the same day, show as single date
  if (range.to && range.from.getTime() === range.to.getTime()) {
    return from;
  }
  
  return `${from} → ${to}`;
}

function Counter({
  label,
  value,
  min = 0,
  max = 10,
  onChange,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
}) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={dec}
          disabled={value <= min}
          className="w-8 h-8 cursor-pointer rounded-full border flex items-center justify-center disabled:cursor-auto disabled:opacity-40"
          aria-label={`Decrease ${label}`}
        >
          −
        </button>
        <span className="w-6 text-center">{value}</span>
        <button
          type="button"
          onClick={inc}
          disabled={value >= max}
          className="w-8 h-8 rounded-full cursor-pointer border flex items-center justify-center disabled:cursor-auto disabled:opacity-40"
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

export default function BookingStays() {
  const [hotel, setHotel] = React.useState<Hotel | null>(null);
  const [range, setRange] = React.useState<DateRange | undefined>();
  const [guests, setGuests] = React.useState({ adults: 2 });
  const [isCheckingAvailability, setIsCheckingAvailability] =
    React.useState(false);

  const [openHotel, setOpenHotel] = React.useState(false);
  const [openDates, setOpenDates] = React.useState(false);
  const [openGuests, setOpenGuests] = React.useState(false);

  const anyOpen = openHotel || openDates || openGuests;
  const hotelLabel = hotel?.name ?? "Choose a location";
  const datesLabel = formatRange(range);
  const guestsCount = guests.adults;
  const guestsLabel = `${guestsCount} ${guestsCount === 1 ? "guest" : "guests"}`;

  const hotelActive = !!hotel;
  const datesActive = !!range?.from;
  const guestsActive = guestsCount > 0;

  const today = new Date();
  const maxDate = new Date(today);
  maxDate.setFullYear(today.getFullYear() + 1);

  function handleRangeSelect(next?: DateRange) {
    if (!next) return setRange(undefined);
    const from = next.from && next.from < today ? today : next.from;
    let to = next.to;
    
    // If no end date is selected, or if start and end are the same, set end to next day
    if (!to || (from && to && from.getTime() === to.getTime())) {
      to = from ? new Date(from) : undefined;
      if (to) {
        to.setDate(to.getDate() + 1);
      }
    }
    
    if (to && to > maxDate) to = maxDate;
    setRange(from ? { from, to } : undefined);
  }

  const handleCheckAvailability = React.useCallback(async () => {
    if (!hotel || !range?.from || !range?.to) return;

    setIsCheckingAvailability(true);

    try {
      const configurationId = getHotelConfigurationId(hotel.id);
      if (!configurationId) {
        console.error(
          `Missing Mews configurationId mapping for hotel id "${hotel.id}"`,
        );
        return;
      }

      const api = await getMews();

      const atLocalNoon = (d: Date) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12);

      api.setStartDate(atLocalNoon(range.from));
      api.setEndDate(atLocalNoon(range.to));
      api.setAdultCount(guests.adults);
      api.showRooms(configurationId);
      api.open();
    } finally {
      setIsCheckingAvailability(false);
    }
  }, [hotel, range, guests]);

  const ctaEnabled =
    !!hotel &&
    !!range?.from &&
    !!range?.to &&
    guests.adults >= 1 &&
    !isCheckingAvailability;

  return (
    <div
      className={cls(
        "w-fit rounded-4xl hidden md:flex flex-row justify-between p-1.5 items-center drop-shadow-md mx-auto",
        anyOpen ? "bg-grey-100" : "bg-white",
      )}
    >
      <div className="flex flex-row gap-2">
        {/* Hotel picker */}
        <Popover.Root open={openHotel} onOpenChange={setOpenHotel}>
          <Popover.Trigger asChild>
            <button
              className="flex-[1.3] cursor-pointer focus:outline-none relative"
              aria-expanded={openHotel}
              aria-haspopup="dialog"
            >
              {!openHotel && (
                <span className="w-[0.1px] h-6 absolute right-0 top-1/2 -translate-y-1/2 bg-grey-400" />
              )}
              <Pill
                label="Hotel"
                value={hotelLabel}
                active={hotelActive}
                highlight={openHotel}
              />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="w-[560px] rounded-2xl bg-white shadow-2xl p-4 outline-none z-[60] flex flex-col gap-2"
              sideOffset={8}
              align="start"
            >
              <div className="text-xs font-medium text-grey-900 px-2 py-1">
                Stays
              </div>
              <ul className="divide-y rounded-xl mx-1 bg-white/70">
                {STAYS.map((h) => {
                  const active = hotel?.id === h.id;
                  return (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setHotel(h);
                          setOpenHotel(false);
                        }}
                        className={cls(
                          "w-full text-left cursor-pointer p-4 hover:bg-grey-100 rounded-xl outline-none",
                          active && "bg-grey-100",
                        )}
                        aria-pressed={active}
                      >
                        <div className="flex flex-row gap-1 items-center">
                          <div className="flex-1 flex flex-col gap-1">
                            <div className="text-xl leading-7 font-medium text-grey-900 tracking-tight">
                              {h.name}
                            </div>
                            {h.reviews && (
                              <div className="text-grey-700 text-xs">
                                {h.reviews}
                              </div>
                            )}
                          </div>
                          {h.status && (
                            <div className="text-gray-700 font-medium text-xs">
                              {h.status}
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* Date range */}
        <Popover.Root open={openDates} onOpenChange={setOpenDates}>
          <Popover.Trigger asChild>
            <button
              className="flex-[1.2] cursor-pointer focus:outline-none relative"
              aria-expanded={openDates}
              aria-haspopup="dialog"
            >
              {!openDates && (
                <span className="w-[0.1px] h-6 absolute right-0 top-1/2 -translate-y-1/2 bg-grey-400" />
              )}
              <Pill
                label="Dates"
                value={datesLabel}
                active={datesActive}
                highlight={openDates}
                className="pr-10"
              />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="rounded-2xl bg-white shadow-2xl p-3 outline-none z-[60]"
              sideOffset={8}
              align="center"
            >
              <DayPicker
                mode="range"
                selected={range}
                onSelect={handleRangeSelect}
                numberOfMonths={2}
                defaultMonth={new Date()}
                disabled={{ before: today, after: maxDate }}
                fromDate={today}
                toDate={maxDate}
                className="rdp-root"
              />
              <div className="mt-3 flex gap-2 justify-end">
                <button
                  type="button"
                  className="text-sm underline underline-offset-2"
                  onClick={() => setRange(undefined)}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="text-sm px-3 py-1 rounded-full border"
                  onClick={() => setOpenDates(false)}
                >
                  Done
                </button>
              </div>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* Guests */}
        <Popover.Root open={openGuests} onOpenChange={setOpenGuests}>
          <Popover.Trigger asChild>
            <button
              className="flex-1 cursor-pointer focus:outline-none"
              aria-expanded={openGuests}
              aria-haspopup="dialog"
            >
              <Pill
                label="Who"
                value={guestsLabel}
                active={guestsActive}
                highlight={openGuests}
              />
            </button>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className="rounded-2xl bg-white shadow-2xl p-4 outline-none w-72 z-[60]"
              sideOffset={8}
              align="end"
            >
              <Counter
                label="Adults"
                value={guests.adults}
                min={1}
                onChange={(v) => setGuests((g) => ({ ...g, adults: v }))}
              />
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  className="text-sm cursor-pointer px-3 py-1 rounded-full border"
                  onClick={() => setOpenGuests(false)}
                >
                  Done
                </button>
              </div>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* CTA */}
        <button
          type="button"
          aria-label="Check availability"
          onClick={handleCheckAvailability}
          disabled={!ctaEnabled}
          className={cls(
            "cursor-pointer disabled:cursor-not-allowed disabled:shadow-none shrink-0 group relative z-10 py-4 px-6 flex items-center gap-2 font-medium rounded-[28px] text-white hover:shadow-lg",
            ctaEnabled ? "bg-black" : "bg-gray-400 cursor-not-allowed",
          )}
        >
          <span className="absolute inset-0 rounded-[28px] group-disabled:opacity-0 border-2 border-black opacity-0 transition group-hover:opacity-100 group-hover:scale-[1.02]" />
          {isCheckingAvailability ? (
            <>
              <Spinner />
              Checking...
            </>
          ) : (
            "Check availability"
          )}
        </button>
      </div>
    </div>
  );
}
